import { Box, Grid, Theme, alpha, useTheme } from '@mui/material';
import { MinorHeading, Subtitle } from '../shared/typography/PrimaryTypography';
import YoutubeVideo from '../shared/template/YoutubeVideo';
import video_placeholder from '../../assets/landing_page/video_placeholder.webp';
import why_syntiro_thumbnail from '../../assets/landing_page/why-syntiro-video-thumbnail.webp';
import intuitive_sustainability_thumbnail from '../../assets/landing_page/intuitive-sustainability-video-thumbnail.webp';
import syntiro_in_action from '../../assets/landing_page/syntiro-in-action-thumbnail.webp';

interface VideoResource {
  description: string;
  published: boolean;
  youtube_id?: string;
  thumbnail?: string;
}

const resources: Array<VideoResource> = [
  {
    description:
      'See Syntiro up close and discover how its capabilities support both operations and the boardroom.',
    published: true,
    youtube_id: 'D41tc3eHvVE',
    thumbnail: syntiro_in_action
  },
  {
    description: `See how Syntiro turns live data into audit-grade ESG insights.`,
    published: true,
    youtube_id: '6pUGO0f_UTc',
    thumbnail: intuitive_sustainability_thumbnail
  },
  {
    description: 'Discover why our clients are using Syntiro',
    published: true,
    youtube_id: 'dPi11bui1Qw',
    thumbnail: why_syntiro_thumbnail
  }
];

/**
 * Section promoting core Energy Partners brand and website.
 * @returns {JSX.Element}
 */
const ResourcesSection = () => {
  const theme: Theme = useTheme();
  return (
    <Box id="resources">
      <Grid container spacing={4} sx={{ justifyContent: 'center', px: { xs: 2, md: 6 }, pb: 2 }}>
        <Grid size={12}>
          <Subtitle weight="Bold">Resources</Subtitle>
        </Grid>
        {resources.map((resource: VideoResource, i: number) => (
          <Grid key={i} size={{ xs: 12, md: 6, lg: 4 }} sx={{ textAlign: 'left' }}>
            <Box
              sx={{
                boxSizing: 'border-box',
                position: 'relative',
                maxWidth: '800px',
                minWidth: '320px',
                mx: 'auto',
                pb: '56%', // 16:9 ratio
                height: 0,
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: `0px 0px 1px 1px ${theme.palette.secondary.dark}`
              }}
            >
              {resource.youtube_id && resource.published ? (
                <YoutubeVideo
                  video_id={resource.youtube_id}
                  thumbnail={resource.thumbnail}
                  play_in_modal
                />
              ) : (
                <Box
                  sx={{
                    position: 'relative',
                    backgroundImage: `url(${resource.thumbnail ? resource.thumbnail : video_placeholder})`,
                    backgroundRepeat: 'no-repeat',
                    width: '100%',
                    mx: 'auto',
                    pb: '56%', // 16:9 ratio
                    backgroundSize: 'cover',
                    borderRadius: '12px'
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                      borderRadius: '12px'
                    }}
                  />
                </Box>
              )}
            </Box>
            <MinorHeading sx={{ color: theme.palette.secondary.dark, my: 1.5 }}>
              {resource.description}
            </MinorHeading>
          </Grid>
        ))}
        <Grid size={12}>
          <MinorHeading
            weight="Medium"
            sx={{ color: theme.palette.teal.main, textAlign: 'left', my: 2 }}
          >
            Visit our{' '}
            <strong>
              <a
                href="https://energypartners.co.za/"
                target="_blank/"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', color: theme.palette.teal.dark }}
              >
                website
              </a>
            </strong>{' '}
            for more information about Energy Partners.
          </MinorHeading>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ResourcesSection;
